"use client";

import NextLink from "next/link";
import { Building2, MapPin, Receipt, ShoppingCart } from "lucide-react";
import type { SearchResultDto, MapSearchResponseDto, SearchPagedResponseDtoTotals } from "@/lib/api/apiwrapper";
import type { SearchContext } from "./filterSuggestions";
import { useTranslations } from "next-intl";
import Loader from "@/components/ui/Loader";
import { Heading } from "@/components/typography/Heading";
import { Link } from "@/i18n/navigation";
import { HorizontalContainer } from "@/components/layout/containers/HorizontalContainer";

type Props = {
  context: SearchContext;
  searchText: string;
  hasTokens: boolean;
  spendResults: SearchResultDto & { totals?: SearchPagedResponseDtoTotals | null };
  mapResults: MapSearchResponseDto;
  onResultClick?: () => void;
  isSearching?: boolean;
};

/**
 * Renders search results based on the active context.
 * Spend context: companies + orders lists.
 * Map context: cities + companies lists.
 */
export function SearchResultsDropdown({ context, searchText, hasTokens, spendResults, mapResults, onResultClick, isSearching }: Props) {
  const t = useTranslations();
  const MINCHARACTERS = 3

  const hasQuery = searchText.length >= MINCHARACTERS || hasTokens;

  if (isSearching) {
    return <SearchLoadingState />;
  }

  if (!hasQuery) {
    return <HorizontalContainer className="flex-grow justify-center p-8 text-center text-sm text-muted-foreground">
        {searchText.length > 0 && searchText.length < MINCHARACTERS ? t("search.typeAtLeast", { characters: MINCHARACTERS }) : t("search.startTypingToSearch")}
      </HorizontalContainer>;
  }

  if (context === "spend") {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex-grow overflow-y-auto">
          <SpendResults results={spendResults} searchText={searchText} onResultClick={onResultClick} />
        </div>
        <SearchDropdownFooter query={searchText} onResultClick={onResultClick} />
      </div>
    );
  }

  return (
    <MapResults
      results={mapResults}
      onResultClick={onResultClick}
    />
  );
}

// ─── Spend Results ────────────────────────────────────────

function SpendResults({ results, searchText, onResultClick }: { results: SearchResultDto & { totals?: SearchPagedResponseDtoTotals | null }; searchText: string; onResultClick?: () => void }) {
  const t = useTranslations();
  const hasResults = (results.companies?.length ?? 0) > 0 || (results.orders?.length ?? 0) > 0 || (results.invoices?.length ?? 0) > 0;

  if (!hasResults) {
    return <HorizontalContainer className="flex-grow justify-center p-8 text-center text-sm text-muted-foreground">{t("search.noResultsFound")}</HorizontalContainer>;
  }

  const totals = results.totals || {};

  return (
    <div className="flex-grow divide-y divide-border">
      {results.companies && results.companies.length > 0 && (
        <CategorySection
          title={t("companies.titlePlural")}
          count={totals["companies"]}
          category="Companies"
          query={searchText}
          onResultClick={onResultClick}
        >
          {results.companies.slice(0, 3).map((company) => (
            <Link
              key={company.id}
              href={{
                pathname: "/explore/company/[id]",
                params: {id: company.id ?? ""}
              }}
              className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent group"
              onClick={onResultClick}
            >
              <Building2 className="mr-2 h-4 w-4 text-muted-foreground group-hover:text-primary" />
              <div className="flex flex-col">
                <span className="font-medium">{company.name}</span>
                {company.city && <span className="text-[11px] text-muted-foreground">{company.city}</span>}
              </div>
            </Link>
          ))}
        </CategorySection>
      )}

      {results.orders && results.orders.length > 0 && (
        <CategorySection
          title={t("orders.title")}
          count={totals["orders"]}
          category="Orders"
          query={searchText}
          onResultClick={onResultClick}
        >
          {results.orders.slice(0, 3).map((order) => (
            <NextLink
              key={order.id}
              href={`/explore/order/${order.id}`}
              className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent group"
              onClick={onResultClick}
            >
              <ShoppingCart className="mr-2 h-4 w-4 text-muted-foreground group-hover:text-primary" />
              <div className="flex flex-col">
                <span className="font-medium">{order.description || order.externalOrderId || order.id}</span>
                {order.externalOrderId && <span className="text-[11px] text-muted-foreground">{order.externalOrderId}</span>}
              </div>
            </NextLink>
          ))}
        </CategorySection>
      )}

      {results.invoices && results.invoices.length > 0 && (
        <CategorySection
          title={t("invoices.titlePlural")}
          count={totals["invoices"]}
          category="Invoices"
          query={searchText}
          onResultClick={onResultClick}
        >
          {results.invoices.slice(0, 3).map((invoice) => (
            <NextLink
              key={invoice.id}
              href={`/explore/invoice/${invoice.id}`}
              className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent group"
              onClick={onResultClick}
            >
              <Receipt className="mr-2 h-4 w-4 text-muted-foreground group-hover:text-primary" />
              <div className="flex flex-col">
                <span className="font-medium">{invoice.description || invoice.invoiceNumber || invoice.id}</span>
                {invoice.invoiceNumber && <span className="text-[11px] text-muted-foreground">{invoice.invoiceNumber}</span>}
              </div>
            </NextLink>
          ))}
        </CategorySection>
      )}
    </div>
  );
}

function CategorySection({ title, count, category, query, onResultClick, children }: { title: string; count?: number; category: string; query: string; onResultClick?: () => void; children: React.ReactNode }) {
  const t = useTranslations();
  const showSeeAll = count !== undefined && count > 3;

  return (
    <div className="py-2">
      <div className="px-4 py-1 flex items-center justify-between">
        <h3 className="text-[10px] font-medium tracking-[0.07em] uppercase text-muted-foreground">{title}</h3>
        {showSeeAll && (
          <NextLink
            href={`/search?q=${encodeURIComponent(query)}&category=${category}`}
            className="text-[11px] text-primary hover:bg-[#E6F1FB] px-2 py-0.5 rounded-[4px] transition-colors"
            onClick={onResultClick}
          >
            {t("search.seeAll", { count })}
          </NextLink>
        )}
      </div>
      {children}
    </div>
  );
}

function SearchDropdownFooter({ query, onResultClick }: { query: string; onResultClick?: () => void }) {
  const t = useTranslations();
  return (
    <NextLink
      href={`/search?q=${encodeURIComponent(query)}`}
      className="sticky bottom-0 bg-accent/50 border-t border-border px-4 py-2 flex items-center gap-2 hover:bg-accent transition-colors"
      onClick={onResultClick}
    >
      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary/10">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M5 12h14m-7-7 7 7-7 7"/></svg>
      </div>
      <span className="text-sm text-primary flex-grow">{t("search.viewAllResults", { query })}</span>
      <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
        <span className="text-xs">↵</span>
      </kbd>
    </NextLink>
  );
}

// ─── Map Results ──────────────────────────────────────────

function MapResults({ results, onResultClick }: { results: MapSearchResponseDto; onResultClick?: () => void }) {
  const t = useTranslations();
  const hasResults = (results.cities?.length ?? 0) > 0 || (results.companies?.length ?? 0) > 0;

  if (!hasResults) {
    return <HorizontalContainer className="flex-grow justify-center p-8 text-center text-sm text-muted-foreground">{t("search.noResultsFound")}</HorizontalContainer>;
  }

  return (
    <div className="flex-grow divide-y divide-border">
      {results.cities && results.cities.length > 0 && (
        <div className="py-2">
          <Heading as="h3" size="xs" className="px-4 py-1 uppercase text-muted-foreground">{t("general.cities")}</Heading>
          {results.cities.map((city) => (
            <NextLink
              key={city.name}
              href={`/map?lat=${city.placeDetails?.location?.latitude}&lon=${city.placeDetails?.location?.longitude}`}
              className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent"
              onClick={onResultClick}
            >
              <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
              {city.name}
            </NextLink>
          ))}
        </div>
      )}

      {results.companies && results.companies.length > 0 && (
        <div className="py-2">
          <Heading as="h3" size="xs" className="px-4 py-1 uppercase text-muted-foreground">{t("companies.titlePlural")}</Heading>
          {results.companies.map((company) => (
            <NextLink
              key={company.id}
              href={`/map?lat=${company.lat}&lon=${company.lon}&companyId=${company.id}&zoom=18`}
              className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-accent"
              onClick={onResultClick}
            >
              <MapPin className="mr-2 h-4 w-4 text-muted-foreground" />
              {company.name}
            </NextLink>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchLoadingState() {
  const t = useTranslations();
  return (
    <HorizontalContainer className="flex-grow justify-center p-8 text-center text-muted-foreground">
      <Loader title={t("search.loadingResults")} />
    </HorizontalContainer>
  );
}
